import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';
import { api } from '@/App';

// Light blue palette (table body)
const HEADER_FILL = [191, 219, 254];   // #bfdbfe (blue-200)
const HEADER_TEXT = [30, 64, 175];     // #1e40af (blue-800)
const BODY_ALT_FILL = [239, 246, 255]; // #eff6ff (blue-50)
const BORDER = [147, 197, 253];        // #93c5fd (blue-300)

// Yellow palette (section titles + summary)
const YELLOW_FILL = [254, 240, 138];   // #fef08a (yellow-200)
const YELLOW_TEXT = [113, 63, 18];     // #713f12 (yellow-900)
const YELLOW_BORDER = [234, 179, 8];   // #eab308 (yellow-500)

const fmtRp = (v) => 'Rp. ' + Math.round(Number(v) || 0).toLocaleString('id-ID');
const fmtNum = (v, digits = 0) =>
  (Number(v) || 0).toLocaleString('id-ID', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
const fmtDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('id-ID', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return String(d);
  }
};

const sectionTitle = (doc, text, y) => {
  doc.setFillColor(...YELLOW_FILL);
  doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 8, 'F');
  doc.setTextColor(...YELLOW_TEXT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(text, 16, y + 5.5);
  doc.setTextColor(0, 0, 0);
  return y + 12;
};

const addTable = (doc, head, body, startY, foot) => {
  autoTable(doc, {
    startY,
    head: [head],
    body,
    foot,
    showFoot: 'lastPage',
    theme: 'grid',
    styles: {
      fontSize: 7.5,
      cellPadding: 1.8,
      lineColor: BORDER,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: HEADER_FILL,
      textColor: HEADER_TEXT,
      fontStyle: 'bold',
      lineColor: BORDER,
    },
    footStyles: {
      fillColor: HEADER_FILL,
      textColor: HEADER_TEXT,
      fontStyle: 'bold',
    },
    alternateRowStyles: { fillColor: BODY_ALT_FILL },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY;
};

// Rekapan-specific table: 18 columns, no text wrapping on currency cells.
// Uses smaller font + horizontal scaling (tableWidth: 'auto') and lets autotable
// auto-shrink columns. overflow: 'visible' disables line wrapping inside cells.
const addRekapanTable = (doc, head, body, startY, foot) => {
  autoTable(doc, {
    startY,
    head: [head],
    body,
    foot,
    showFoot: 'lastPage',
    theme: 'grid',
    tableWidth: 'auto',
    styles: {
      fontSize: 6,
      cellPadding: 1.2,
      lineColor: BORDER,
      lineWidth: 0.1,
      overflow: 'visible',
      cellWidth: 'wrap',
    },
    headStyles: {
      fillColor: HEADER_FILL,
      textColor: HEADER_TEXT,
      fontStyle: 'bold',
      lineColor: BORDER,
      overflow: 'linebreak',  // allow header to wrap multi-word labels
      halign: 'center',
    },
    footStyles: {
      fillColor: HEADER_FILL,
      textColor: HEADER_TEXT,
      fontStyle: 'bold',
      overflow: 'visible',
    },
    bodyStyles: { halign: 'right' },
    columnStyles: {
      0: { halign: 'left', fontStyle: 'bold' }, // Partai
    },
    alternateRowStyles: { fillColor: BODY_ALT_FILL },
    margin: { left: 8, right: 8 },
  });
  return doc.lastAutoTable.finalY;
};

const addSummaryKeyValueTable = (doc, body, startY, margin) => {
  autoTable(doc, {
    startY,
    body,
    theme: 'grid',
    styles: {
      fontSize: 10,
      cellPadding: 3,
      lineColor: YELLOW_BORDER,
      lineWidth: 0.2,
    },
    bodyStyles: {
      fillColor: YELLOW_FILL,
      textColor: YELLOW_TEXT,
      fontStyle: 'bold',
      lineColor: YELLOW_BORDER,
    },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'right' },
    },
    margin,
  });
  return doc.lastAutoTable.finalY;
};

const addMitraDividendTable = (doc, head, body, startY, margin) => {
  autoTable(doc, {
    startY,
    head: [head],
    body,
    theme: 'grid',
    styles: {
      fontSize: 10,
      cellPadding: 3,
      lineColor: YELLOW_BORDER,
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: YELLOW_FILL,
      textColor: YELLOW_TEXT,
      fontStyle: 'bold',
      lineColor: YELLOW_BORDER,
      halign: 'center',
    },
    bodyStyles: {
      fillColor: YELLOW_FILL,
      textColor: YELLOW_TEXT,
      fontStyle: 'bold',
      lineColor: YELLOW_BORDER,
    },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'center' },
      2: { halign: 'right' },
    },
    margin,
  });
  return doc.lastAutoTable.finalY;
};

const ensureRoom = (doc, y, needed = 30) => {
  if (y > doc.internal.pageSize.getHeight() - needed) {
    doc.addPage();
    return 14;
  }
  return y;
};

// Compute share % + per-mitra rows for a branch, honoring exception list & toggle.
const computeShares = (allManagers, branchId, pengelolaId, showAllSharesGlobal, exceptionBranches) => {
  const isException = (exceptionBranches || []).includes(branchId);
  const showAll = showAllSharesGlobal || isException;
  const entries = [];
  let total = 0;
  allManagers.forEach((m) => {
    const share = (m.branch_shares || []).find((s) => s.branch_id === branchId);
    if (!share) return;
    if (!showAll && m.id !== pengelolaId) return;
    const pct = Number(share.percentage) || 0;
    if (pct <= 0) return;
    entries.push({ name: m.name || '-', pct });
    total += pct;
  });
  entries.sort((a, b) => b.pct - a.pct);
  return { entries, total };
};

// Saham + dividen mitra dari SNAPSHOT periode tertutup (saham bisa berubah setelah tutup buku).
const sharesFromSnapshot = (snapRow, pengelolaId, showAllSharesGlobal, exceptionBranches, branchId) => {
  const isException = (exceptionBranches || []).includes(branchId);
  const showAll = showAllSharesGlobal || isException;
  const perMitra = snapRow.mitra_dividend_per_mid || {};
  const perManager = snapRow.manager_dividend_per_mid || {};
  const entries = [];
  (snapRow.shareholders || []).forEach((sh) => {
    if (!showAll && sh.manager_id !== pengelolaId) return;
    const pct = Number(sh.percentage) || 0;
    if (pct <= 0) return;
    entries.push({
      name: sh.manager_name || '-',
      pct,
      dividend: perMitra[sh.manager_id] ?? perManager[sh.manager_id] ?? 0,
    });
  });
  entries.sort((a, b) => b.pct - a.pct);
  return entries;
};

// Ambil baris dividen per cabang dari snapshot tutup buku (hanya untuk periode tertutup).
const fetchSnapshotRows = async (periodId, selectedPeriod) => {
  if (!periodId || !selectedPeriod?.is_closed) return {};
  try {
    const res = await api.get(`/periods/${periodId}/snapshot`);
    if (!res.data?.has_snapshot) return {};
    const map = {};
    (res.data.branch_dividends || []).forEach((b) => { map[b.branch_id] = b; });
    return map;
  } catch (error) {
    console.error('Failed to fetch period snapshot for PDF:', error);
    return {};
  }
};

// Renders one branch's full report onto an existing jsPDF doc starting at y.
// Does NOT save. Returns the new y value (after rendering).
async function renderBranchSections(doc, ctx) {
  const {
    branchId, branchName, periodName, historyYear,
    allPurchases, allExpenses, allSales, allManagers,
    pengelolaId, showAllSharesGlobal, exceptionBranches, opCostPerGr,
    snapshotBranchRow,
  } = ctx;
  const pageW = doc.internal.pageSize.getWidth();

  // Header banner
  doc.setFillColor(...HEADER_FILL);
  doc.rect(0, 0, pageW, 22, 'F');
  doc.setTextColor(...HEADER_TEXT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(`Transaksi Cabang — ${branchName}`, 14, 10);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Periode: ${periodName || '-'}`, 14, 16);
  doc.text(
    `Dicetak: ${new Date().toLocaleString('id-ID')}`,
    pageW - 14, 16, { align: 'right' }
  );
  doc.setTextColor(0, 0, 0);

  let y = 28;

  // Summary tables side-by-side
  const totalPembelian = allPurchases.reduce((s, p) => s + (Number(p.total_amount) || 0), 0);
  const totalBiaya = allExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const totalPenjualan = allSales.reduce((s, s2) => s + (Number(s2.total_amount) || 0), 0);
  const labaRugi = totalPenjualan - totalBiaya - totalPembelian;

  const mitraRows = snapshotBranchRow
    ? sharesFromSnapshot(snapshotBranchRow, pengelolaId, showAllSharesGlobal, exceptionBranches, branchId)
        .map((e) => [e.name, e.pct.toFixed(0) + '%', fmtRp(Math.round(e.dividend))])
    : computeShares(allManagers, branchId, pengelolaId, showAllSharesGlobal, exceptionBranches)
        .entries.map((e) => [e.name, e.pct.toFixed(0) + '%', fmtRp(Math.round((e.pct / 100) * labaRugi))]);

  const halfW = pageW / 2;
  const leftEndY = addSummaryKeyValueTable(
    doc,
    [
      ['Pembelian', fmtRp(totalPembelian)],
      ['Biaya', fmtRp(totalBiaya)],
      ['Penjualan', fmtRp(totalPenjualan)],
      ['Laba / Rugi', fmtRp(labaRugi)],
    ],
    y,
    { left: 14, right: halfW + 6 }
  );
  const rightEndY = addMitraDividendTable(
    doc,
    ['Mitra', 'Saham', 'Dividen'],
    mitraRows.length > 0 ? mitraRows : [['-', '-', '-']],
    y,
    { left: halfW + 6, right: 14 }
  );
  y = Math.max(leftEndY, rightEndY) + 8;

  // 1. PEMBELIAN
  y = ensureRoom(doc, y);
  y = sectionTitle(doc, '1. Pembelian', y);
  if (allPurchases.length === 0) {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic');
    doc.text('Belum ada data pembelian.', 16, y); y += 8;
    doc.setFont('helvetica', 'normal');
  } else {
    const head = ['Tanggal', 'Supplier', 'Partai', 'Nama Bahan', 'Berat (gr)', 'Harga/gr', 'Subtotal', 'Keterangan'];
    const body = [];
    allPurchases.forEach((p) => {
      (p.items || []).forEach((it) => {
        body.push([
          fmtDate(p.purchase_date),
          p.supplier_name || '-',
          p.partai || '-',
          it.name || '-',
          fmtNum(it.weight_kg, 2),
          fmtRp(it.price_per_kg),
          fmtRp(it.subtotal),
          it.keterangan || it.notes || '-',
        ]);
      });
    });
    const totalBerat = allPurchases.reduce((s, p) =>
      s + (p.items || []).reduce((ss, it) => ss + (Number(it.weight_kg) || 0), 0), 0);
    const foot = [['', '', '', 'TOTAL', fmtNum(totalBerat, 2), '', fmtRp(totalPembelian), '']];
    y = addTable(doc, head, body, y, foot) + 6;
  }

  // 2. BIAYA
  y = ensureRoom(doc, y);
  y = sectionTitle(doc, '2. Biaya Operasional', y);
  if (allExpenses.length === 0) {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic');
    doc.text('Belum ada data biaya.', 16, y); y += 8;
    doc.setFont('helvetica', 'normal');
  } else {
    const head = ['Tanggal', 'Partai', 'Jumlah', 'Deskripsi'];
    const body = allExpenses.map((e) => [
      fmtDate(e.expense_date),
      e.partai || '-',
      fmtRp(e.amount),
      e.description || '-',
    ]);
    const foot = [['', 'TOTAL', fmtRp(totalBiaya), '']];
    y = addTable(doc, head, body, y, foot) + 6;
  }

  // 3. PENJUALAN
  y = ensureRoom(doc, y);
  y = sectionTitle(doc, '3. Penjualan', y);
  if (allSales.length === 0) {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic');
    doc.text('Belum ada data penjualan.', 16, y); y += 8;
    doc.setFont('helvetica', 'normal');
  } else {
    const head = ['Tanggal', 'Customer', 'Partai', 'Nama Bahan', 'Berat (gr)', 'Harga/gr', 'Subtotal', 'Keterangan'];
    const body = allSales.map((s) => [
      fmtDate(s.sale_date),
      s.customer_name || '-',
      s.partai || '-',
      s.product_name || '-',
      fmtNum(s.quantity_kg, 2),
      fmtRp(s.price_per_kg),
      fmtRp(s.total_amount),
      s.keterangan || '-',
    ]);
    const totalBeratJual = allSales.reduce((s, sl) => s + (Number(sl.quantity_kg) || 0), 0);
    const foot = [['', '', '', 'TOTAL', fmtNum(totalBeratJual, 2), '', fmtRp(totalPenjualan), '']];
    y = addTable(doc, head, body, y, foot) + 6;
  }

  // 4. REKAPAN per Partai
  y = ensureRoom(doc, y);
  y = sectionTitle(doc, '4. Rekapan per Partai', y);
  const NO_PARTAI = '(Tanpa Partai)';
  const partaiSet = [...new Set(allPurchases.map((p) => p.partai).filter(Boolean))];
  const hasNoPartai = allPurchases.some((p) => !p.partai) || allSales.some((s) => !s.partai) || allExpenses.some((e) => !e.partai);
  const partaiList = hasNoPartai ? [...partaiSet.sort(), NO_PARTAI] : partaiSet.sort();
  if (partaiList.length === 0) {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic');
    doc.text('Belum ada partai pada periode ini.', 16, y); y += 8;
    doc.setFont('helvetica', 'normal');
  } else {
    const head = [
      'Partai', 'Stok Awal (gr)', 'Berat Beli (gr)', 'Berat Jual (gr)',
      'Stok Akhir (gr)', '+/- gr', '+/- %',
      'Rp Stok Awal', 'Rp Beli', 'Rp Jual', 'Rp Stok Akhir',
      'Biaya OP', 'Biaya OP/gr', 'Biaya Pusat',
      'Laba Rugi', 'Laba Rugi/gr',
    ];
    const body = [];
    const totals = {
      stokAwal: 0, beratBeli: 0, beratJual: 0, stokAkhir: 0,
      rpStokAwal: 0, rpBeli: 0, rpJual: 0, rpStokAkhir: 0,
      biayaOP: 0, biayaPusat: 0, labaRugi: 0,
    };
    partaiList.forEach((partai) => {
      const isNoPartai = partai === NO_PARTAI;
      const ps = allPurchases.filter((p) => isNoPartai ? !p.partai : p.partai === partai);
      const ss = allSales.filter((s) => isNoPartai ? !s.partai : s.partai === partai);
      const es = allExpenses.filter((e) => isNoPartai ? !e.partai : e.partai === partai);
      const stokAwalGr = ps.reduce((sum, p) =>
        sum + (p.items || []).filter((it) => it.keterangan === 'Stok Awal')
          .reduce((s, it) => s + (Number(it.weight_kg) || 0), 0), 0);
      const totalBeratBeli = ps.reduce((sum, p) =>
        sum + (p.items || []).reduce((s, it) => s + (Number(it.weight_kg) || 0), 0), 0);
      const beratBeli = totalBeratBeli - stokAwalGr;
      const beratJual = ss.filter((s) => s.keterangan !== 'Stok')
        .reduce((sum, s) => sum + (Number(s.quantity_kg) || 0), 0);
      const stokAkhirGr = ss.filter((s) => s.keterangan === 'Stok')
        .reduce((sum, s) => sum + (Number(s.quantity_kg) || 0), 0);
      const plusMinusGr = (beratJual + stokAkhirGr) - (beratBeli + stokAwalGr);
      const denom = beratBeli + stokAwalGr;
      const plusMinusPct = denom > 0 ? (plusMinusGr / denom) * 100 : 0;
      const rpStokAwal = ps.reduce((sum, p) =>
        sum + (p.items || []).filter((it) => it.keterangan === 'Stok Awal')
          .reduce((s, it) => s + (Number(it.subtotal) || 0), 0), 0);
      const totalRpBeli = ps.reduce((sum, p) => sum + (Number(p.total_amount) || 0), 0);
      const rpBeli = totalRpBeli - rpStokAwal;
      const rpJual = ss.filter((s) => s.keterangan !== 'Stok')
        .reduce((sum, s) => sum + (Number(s.total_amount) || 0), 0);
      const rpStokAkhir = ss.filter((s) => s.keterangan === 'Stok')
        .reduce((sum, s) => sum + (Number(s.total_amount) || 0), 0);
      const biayaOP = es.reduce((sum, e) => {
        if (e.description && e.description.includes('Biaya Operasional - Partai')) return sum;
        return sum + (Number(e.amount) || 0);
      }, 0);
      const biayaOPPerGr = beratJual > 0 ? biayaOP / beratJual : 0;
      const biayaPusat = beratJual * opCostPerGr;
      const labaRugiVal = rpJual + rpStokAkhir - rpStokAwal - rpBeli - biayaOP - biayaPusat;
      const labaRugiPerGr = beratJual > 0 ? labaRugiVal / beratJual : 0;
      totals.stokAwal += stokAwalGr; totals.beratBeli += beratBeli;
      totals.beratJual += beratJual; totals.stokAkhir += stokAkhirGr;
      totals.rpStokAwal += rpStokAwal; totals.rpBeli += rpBeli;
      totals.rpJual += rpJual; totals.rpStokAkhir += rpStokAkhir;
      totals.biayaOP += biayaOP; totals.biayaPusat += biayaPusat;
      totals.labaRugi += labaRugiVal;
      body.push([
        partai, fmtNum(stokAwalGr, 0), fmtNum(beratBeli, 0), fmtNum(beratJual, 0),
        fmtNum(stokAkhirGr, 0), fmtNum(plusMinusGr, 0), plusMinusPct.toFixed(2) + '%',
        fmtRp(rpStokAwal), fmtRp(rpBeli), fmtRp(rpJual), fmtRp(rpStokAkhir),
        fmtRp(biayaOP), fmtRp(biayaOPPerGr), fmtRp(biayaPusat),
        fmtRp(labaRugiVal), fmtRp(labaRugiPerGr),
      ]);
    });
    const totDenom = totals.beratBeli + totals.stokAwal;
    const totPct = totDenom > 0
      ? (((totals.beratJual + totals.stokAkhir) - totDenom) / totDenom * 100) : 0;
    const foot = [[
      'TOTAL', fmtNum(totals.stokAwal, 0), fmtNum(totals.beratBeli, 0),
      fmtNum(totals.beratJual, 0), fmtNum(totals.stokAkhir, 0),
      fmtNum((totals.beratJual + totals.stokAkhir) - totDenom, 0),
      totPct.toFixed(2) + '%',
      fmtRp(totals.rpStokAwal), fmtRp(totals.rpBeli),
      fmtRp(totals.rpJual), fmtRp(totals.rpStokAkhir),
      fmtRp(totals.biayaOP), '—', fmtRp(totals.biayaPusat),
      fmtRp(totals.labaRugi), '—',
    ]];
    y = addRekapanTable(doc, head, body, y, foot) + 6;
  }

  // 5. HISTORY
  y = ensureRoom(doc, y);
  y = sectionTitle(doc, `5. History Cabang — Tahun ${historyYear}`, y);
  try {
    const histRes = await api.get(`/branches/${branchId}/yearly-history`, { params: { year: historyYear } });
    const rows = Array.isArray(histRes.data) ? histRes.data : (histRes.data?.rows || []);
    if (rows.length === 0) {
      doc.setFontSize(9); doc.setFont('helvetica', 'italic');
      doc.text('Tidak ada history untuk tahun ini.', 16, y); y += 8;
      doc.setFont('helvetica', 'normal');
    } else {
      const head = ['Bulan', 'Penjualan (gr)', 'Laba / gr', 'Biaya / gr', 'Kadar %'];
      const body = rows.map((r) => [
        r.month_label || '-',
        fmtNum(r.penjualan_gr, 0),
        fmtNum(r.laba_per_gr, 0),
        fmtNum(r.biaya_per_gr, 0),
        ((Number(r.kadar_percent) || 0)).toFixed(2) + ' %',
      ]);
      const totalPenjualanGr = rows.reduce((s, r) => s + (Number(r.penjualan_gr) || 0), 0);
      const foot = [['TOTAL', fmtNum(totalPenjualanGr, 0), '—', '—', '—']];
      y = addTable(doc, head, body, y, foot) + 6;
    }
  } catch (e) {
    doc.setFontSize(9); doc.setFont('helvetica', 'italic');
    doc.text('Gagal memuat history cabang.', 16, y); y += 8;
    doc.setFont('helvetica', 'normal');
  }
  return y;
}

const drawPagination = (doc) => {
  const pageW = doc.internal.pageSize.getWidth();
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(
      `Halaman ${i} / ${totalPages}`,
      pageW - 14,
      doc.internal.pageSize.getHeight() - 6,
      { align: 'right' }
    );
  }
};

const safeName = (s) => String(s || '').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');

export async function exportBranchTransactionsPDF({ branchId, branchName, periodId, periodName }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  // Fetch all data (single branch)
  const params = periodId ? { period_id: periodId } : {};
  const [purchasesRes, expensesRes, salesRes, settingsRes, periodsRes, managersRes, branchesRes] = await Promise.all([
    api.get('/purchases', { params }),
    api.get('/expenses', { params }),
    api.get('/sales', { params }),
    api.get('/settings'),
    api.get('/periods'),
    api.get('/managers'),
    api.get('/branches'),
  ]);
  const allPurchases = purchasesRes.data.filter((p) => p.branch_id === branchId);
  const allExpenses = expensesRes.data.filter((e) => e.branch_id === branchId);
  const allSales = salesRes.data.filter((s) => s.branch_id === branchId);
  const allManagers = managersRes.data || [];
  const branch = (branchesRes.data || []).find((b) => b.id === branchId);
  const pengelolaId = branch?.manager_id || null;
  const showAllSharesGlobal = !!settingsRes.data?.show_non_manager_shares;
  const exceptionBranches = settingsRes.data?.pdf_share_exception_branches || [];
  const selectedPeriod = periodsRes.data.find((p) => p.id === periodId);
  const defaultCost = (selectedPeriod && selectedPeriod.default_operational_cost)
    ? selectedPeriod.default_operational_cost
    : (settingsRes.data?.default_operational_cost || 0);
  const overrides = settingsRes.data?.branch_operational_overrides || [];
  const branchOverride = overrides.find((o) => o.branch_id === branchId);
  const opCostPerGr = branchOverride ? branchOverride.cost_per_gr : defaultCost;
  let historyYear = new Date().getFullYear();
  if (selectedPeriod?.start_date) historyYear = new Date(selectedPeriod.start_date).getFullYear();

  const snapshotRows = await fetchSnapshotRows(periodId, selectedPeriod);

  await renderBranchSections(doc, {
    branchId, branchName, periodName, historyYear,
    allPurchases, allExpenses, allSales, allManagers,
    pengelolaId, showAllSharesGlobal, exceptionBranches, opCostPerGr,
    snapshotBranchRow: snapshotRows[branchId] || null,
  });

  drawPagination(doc);
  doc.save(`${safeName(branchName) || 'cabang'}_${safeName(periodName) || 'periode'}.pdf`);
}

// Multi-branch export. Each branch becomes its own PDF, all bundled into a ZIP.
// onProgress(current, total, branchName) is called between branches.
export async function exportAllBranchesTransactionsPDF({ periodId, periodName, onProgress }) {
  // Fetch everything once
  const params = periodId ? { period_id: periodId } : {};
  const [purchasesRes, expensesRes, salesRes, settingsRes, periodsRes, managersRes, branchesRes] = await Promise.all([
    api.get('/purchases', { params }),
    api.get('/expenses', { params }),
    api.get('/sales', { params }),
    api.get('/settings'),
    api.get('/periods'),
    api.get('/managers'),
    api.get('/branches'),
  ]);

  const branches = branchesRes.data || [];
  const allManagers = managersRes.data || [];
  const showAllSharesGlobal = !!settingsRes.data?.show_non_manager_shares;
  const exceptionBranches = settingsRes.data?.pdf_share_exception_branches || [];
  const defaultCost = settingsRes.data?.default_operational_cost || 0;
  const overrides = settingsRes.data?.branch_operational_overrides || [];
  const selectedPeriod = periodsRes.data.find((p) => p.id === periodId);
  const periodDefaultCost = (selectedPeriod && selectedPeriod.default_operational_cost)
    ? selectedPeriod.default_operational_cost
    : defaultCost;
  let historyYear = new Date().getFullYear();
  if (selectedPeriod?.start_date) historyYear = new Date(selectedPeriod.start_date).getFullYear();

  // Filter branches that have at least one transaction in this period
  const branchesWithData = branches.filter((b) => {
    const hasP = purchasesRes.data.some((p) => p.branch_id === b.id);
    const hasE = expensesRes.data.some((e) => e.branch_id === b.id);
    const hasS = salesRes.data.some((s) => s.branch_id === b.id);
    return hasP || hasE || hasS;
  }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  if (branchesWithData.length === 0) {
    throw new Error('Tidak ada cabang dengan transaksi pada periode ini.');
  }

  const zip = new JSZip();
  const pSafe = safeName(periodName) || 'periode';
  const snapshotRows = await fetchSnapshotRows(periodId, selectedPeriod);

  for (let i = 0; i < branchesWithData.length; i++) {
    const branch = branchesWithData[i];
    if (typeof onProgress === 'function') onProgress(i, branchesWithData.length, branch.name);

    const branchOverride = overrides.find((o) => o.branch_id === branch.id);
    const opCostPerGr = branchOverride ? branchOverride.cost_per_gr : periodDefaultCost;

    // Build a fresh PDF for this branch
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    await renderBranchSections(doc, {
      branchId: branch.id,
      branchName: branch.name,
      periodName,
      historyYear,
      allPurchases: purchasesRes.data.filter((p) => p.branch_id === branch.id),
      allExpenses: expensesRes.data.filter((e) => e.branch_id === branch.id),
      allSales: salesRes.data.filter((s) => s.branch_id === branch.id),
      allManagers,
      pengelolaId: branch.manager_id || null,
      showAllSharesGlobal,
      exceptionBranches,
      opCostPerGr,
      snapshotBranchRow: snapshotRows[branch.id] || null,
    });
    drawPagination(doc);

    const bSafe = safeName(branch.name) || `cabang_${i + 1}`;
    const blob = doc.output('blob');
    zip.file(`${bSafe}_${pSafe}.pdf`, blob);

    // Yield to UI so progress bar can repaint
    await new Promise((res) => setTimeout(res, 0));
  }

  if (typeof onProgress === 'function') {
    onProgress(branchesWithData.length, branchesWithData.length, '');
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Semua_Cabang_${pSafe}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

